import { me } from "@/lib/api/auth";
import {
  getInitialLocale,
  getDirection,
  setLocaleStorage,
} from "@/lib/i18n/locale-store";
import {
  applyThemeClass,
  getInitialTheme,
  setThemeStorage,
  ThemeMode,
} from "@/lib/theme/theme-store";
import { bootstrapSession } from "@/lib/auth/session-store";
import { Locale, SessionState } from "@/types/auth";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ToastProvider } from "@/components/ui/toast";

interface AppContextShape {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  theme: ThemeMode;
  toggleTheme: () => void;
  session: SessionState;
  refreshMe: () => Promise<void>;
  setSession: React.Dispatch<React.SetStateAction<SessionState>>;
}

const AppContext = createContext<AppContextShape | null>(null);

const initialSession: SessionState = {
  status: "idle",
  isAuthenticated: false,
};

export function AppProviders({ children }: { children: React.ReactNode }) {
  const [locale, setLocale] = useState<Locale>("ar");
  const [theme, setTheme] = useState<ThemeMode>("light");
  const [session, setSession] = useState<SessionState>(initialSession);

  useEffect(() => {
    const nextLocale = getInitialLocale();
    setLocale(nextLocale);
    document.documentElement.lang = nextLocale;
    document.documentElement.dir = getDirection(nextLocale);

    const nextTheme = getInitialTheme();
    setTheme(nextTheme);
    applyThemeClass(nextTheme);

    void bootstrapSession().then((state) => setSession(state));
  }, []);

  useEffect(() => {
    const handler = () => {
      setSession({ status: "unauthenticated", isAuthenticated: false });
    };
    window.addEventListener("athr:unauthorized", handler);
    return () => window.removeEventListener("athr:unauthorized", handler);
  }, []);

  const updateLocale = (nextLocale: Locale) => {
    setLocale(nextLocale);
    setLocaleStorage(nextLocale);
    document.documentElement.lang = nextLocale;
    document.documentElement.dir = getDirection(nextLocale);
  };

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      setThemeStorage(next);
      applyThemeClass(next);
      return next;
    });
  };

  const refreshMe = async () => {
    setSession((prev) => ({ ...prev, status: "authenticating" }));
    const response = await me();

    if (response.success && response.data) {
      setSession({
        status: "authenticated",
        isAuthenticated: true,
        user: response.data,
      });
      return;
    }

    setSession({
      status: "unauthenticated",
      isAuthenticated: false,
      errorMessage: response.message,
    });
  };

  const value = useMemo(
    () => ({
      locale,
      setLocale: updateLocale,
      theme,
      toggleTheme,
      session,
      refreshMe,
      setSession,
    }),
    [locale, theme, session],
  );

  return (
    <ToastProvider>
      <AppContext.Provider value={value}>{children}</AppContext.Provider>
    </ToastProvider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used inside AppProviders");
  }
  return context;
}
